/**
 * Analysis Setup — one shared, versioned view of the company.
 *
 * All five analyses read this. Before it existed, each framework would have
 * re-interpreted raw onboarding, deck, interview and data-room records for
 * itself, and five slightly different companies would have been analysed under
 * one name.
 *
 * WHAT A FOUNDER IS ACTUALLY DOING HERE: choosing, per field, which candidate is
 * their current view — and, separately, whether a private-derived value may
 * appear in an investor-visible analysis. Those are two decisions, asked twice,
 * because conflating them is how interview material ends up in front of an
 * investor as a side effect of confirming a value.
 *
 * WHAT AN INVESTOR SEES: the approved investor-safe context bound to the report,
 * read-only. Not the draft — the draft carries candidate provenance, rejected
 * alternatives and private source references, and the endpoint refuses it to
 * anyone without active team membership. This component never receives one.
 */

import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { Check, Info } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { FormInput } from './AnalysisFormPrimitives';
import type {
  DraftCandidate,
  ProvenancedInput,
  StrategyContextDraft,
} from '@/lib/strategic-analysis/types';
import {
  ASSERTION_LABELS,
  FIELD_GROUP_LABELS,
  ORIGIN_LABELS,
  PRECISION_EXPLANATIONS,
  PRECISION_LABELS,
  VERIFICATION_LABELS,
  VISIBILITY_LABELS,
  groupFields,
  labelForField,
  needsPublicationDecision,
  LIST_FIELDS,
  describeInputValue,
  parseFounderAnswer,
  summarizeValue,
} from './field-groups';

export interface ContextSelection {
  fieldKey: string;
  candidateId: string;
  publishToInvestors: boolean;
}

/** A value the founder typed for a field no source could supply. */
export interface FounderInput {
  fieldKey: string;
  value: unknown;
}

interface StrategyContextEditorProps {
  /** Team members only. Absent for an investor. */
  draft?: StrategyContextDraft | null;
  /** The approved investor-safe version. What an investor reads. */
  approvedContext?: { version: number; context: Record<string, ProvenancedInput> } | null;
  canEdit: boolean;
  isSaving?: boolean;
  /**
   * Founder answers travel BESIDE the selections rather than as pseudo-selections:
   * a founder candidate is created server-side during the save, so its id does
   * not exist when the draft is issued and no client could reference it.
   */
  onSave?: (selections: ContextSelection[], founderInputs: FounderInput[]) => void;
  /**
   * Focused mode: only these fields are shown. Used by `Complete analysis
   * inputs` so a founder answers what is blocking rather than re-reading a form
   * they already filled in.
   */
  focusFields?: string[] | null;
  onInspectSource?: (fieldKey: string, sourceKey: string) => void;
}

export function StrategyContextEditor({
  draft,
  approvedContext,
  canEdit,
  isSaving = false,
  onSave,
  focusFields,
  onInspectSource,
}: StrategyContextEditorProps) {
  const colors = useThemeColors();

  const [selections, setSelections] = useState<Record<string, ContextSelection>>(() => {
    const initial: Record<string, ContextSelection> = {};
    for (const [fieldKey, proposed] of Object.entries(draft?.proposed ?? {})) {
      if (!proposed) continue;
      const candidate = (draft?.fields[fieldKey] ?? []).find(
        (c) => c.candidateId === proposed.candidateId,
      );
      initial[fieldKey] = {
        fieldKey,
        candidateId: proposed.candidateId,
        // Never pre-ticked. Publishing private-derived material is a decision a
        // founder makes, not a default they fail to notice.
        publishToInvestors: candidate ? !needsPublicationDecision(candidate) : false,
      };
    }
    return initial;
  });

  /** Raw text per field, kept separate from selections until save. */
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const visibleFieldKeys = useMemo(() => {
    const discovered = draft ? Object.keys(draft.fields) : Object.keys(approvedContext?.context ?? {});
    if (!focusFields || focusFields.length === 0) return discovered;

    // FOCUS FIELDS ARE SHOWN WHETHER OR NOT ANYTHING WAS FOUND FOR THEM, and
    // that is the entire point. `draft.fields` is keyed off the candidates that
    // were actually discovered, so a field nothing was found for has no key —
    // and the fields that block an analysis are, overwhelmingly, exactly those.
    // Filtering the discovered keys by the blocking ones intersected "found
    // something" with "found nothing" and rendered an empty form.
    //
    // A key with no candidates falls through to the "Nothing found for this
    // field" branch and its answer input, which is how a founder unblocks it.
    const focused = focusFields.filter((key) => !discovered.includes(key));
    return [...discovered.filter((key) => focusFields.includes(key)), ...focused];
  }, [draft, approvedContext, focusFields]);

  const groups = useMemo(() => groupFields(visibleFieldKeys), [visibleFieldKeys]);

  // ---- investor / read-only path -------------------------------------------
  if (!draft) {
    return (
      <View className="gap-5">
        <Header
          colors={colors}
          version={approvedContext?.version}
          subtitle="The inputs this analysis was built from, as approved by the startup team."
        />
        {groups.map((group) => (
          <View key={group.id} className="gap-2.5">
            <Text className="text-xs font-semibold uppercase" style={{ color: colors.text.tertiary }}>
              {FIELD_GROUP_LABELS[group.id]}
            </Text>
            {group.fields.map((fieldKey) => {
              const input = approvedContext?.context[fieldKey];
              if (!input) return null;
              return (
                <View
                  key={fieldKey}
                  className="rounded-xl p-3 gap-2"
                  style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, overflow: 'hidden' }}
                >
                  <Text className="text-xs font-semibold" style={{ color: colors.text.tertiary }}>
                    {labelForField(fieldKey)}
                  </Text>
                  <Text className="text-sm leading-5" style={{ color: colors.text.primary }}>
                    {describeInputValue(input)}
                  </Text>
                  <View className="flex-row flex-wrap gap-1.5">
                    <Chip label={ORIGIN_LABELS[input.originLabel]} colors={colors} />
                    <Chip label={VERIFICATION_LABELS[input.verificationLabel]} colors={colors} />
                  </View>
                  {input.detailAvailable && onInspectSource && input.sourceKeys[0] && (
                    <Text
                      accessibilityRole="button"
                      onPress={() => onInspectSource(fieldKey, input.sourceKeys[0])}
                      className="text-sm font-semibold"
                      style={{ color: colors.primary[700] }}
                    >
                      View source
                    </Text>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    );
  }

  // ---- team-member path -----------------------------------------------------
  const select = (fieldKey: string, candidate: DraftCandidate) => {
    setSelections((current) => ({
      ...current,
      [fieldKey]: {
        fieldKey,
        candidateId: candidate.candidateId,
        publishToInvestors: current[fieldKey]?.candidateId === candidate.candidateId
          ? current[fieldKey].publishToInvestors
          : !needsPublicationDecision(candidate),
      },
    }));
  };

  /**
   * Answers that are actually answers.
   *
   * Blank text is dropped rather than sent as an empty value: the server turns
   * every founder input into a candidate and an implicit selection, so an empty
   * one would record the founder as having confirmed a blank.
   */
  const founderInputs = (): FounderInput[] =>
    Object.entries(answers).flatMap(([fieldKey, text]) => {
      const value = parseFounderAnswer(fieldKey, text);
      return value === undefined ? [] : [{ fieldKey, value }];
    });

  const togglePublish = (fieldKey: string) => {
    setSelections((current) => {
      const existing = current[fieldKey];
      if (!existing) return current;
      return {
        ...current,
        [fieldKey]: { ...existing, publishToInvestors: !existing.publishToInvestors },
      };
    });
  };

  return (
    <View className="gap-5">
      <Header
        colors={colors}
        version={draft.previousApprovedVersion?.version}
        subtitle="All five analyses read these values. Choose the version that is your current view, and decide what may appear in an investor-visible analysis."
      />

      {draft.sourcesChanged && (
        <View className="rounded-xl p-3.5" style={{ backgroundColor: colors.amber.light }}>
          <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
            Your profile, deck, interview or data room changed since this setup was approved. Review
            the updated values below. Nothing changes in a published analysis until you save a new
            version.
          </Text>
        </View>
      )}

      {groups.map((group) => (
        <View key={group.id} className="gap-2.5">
          <Text className="text-xs font-semibold uppercase" style={{ color: colors.text.tertiary }}>
            {FIELD_GROUP_LABELS[group.id]}
          </Text>
          {group.fields.map((fieldKey) => {
            const candidates = draft.fields[fieldKey] ?? [];
            const selection = selections[fieldKey];
            const chosen = candidates.find((c) => c.candidateId === selection?.candidateId);
            const conflicted = new Set(candidates.map((c) => c.conflictGroup).filter(Boolean)).size > 0 &&
              candidates.length > 1;

            return (
              <View
                key={fieldKey}
                className="rounded-xl p-3 gap-3"
                style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, overflow: 'hidden' }}
              >
                <View className="flex-row items-center gap-2">
                  <Text numberOfLines={1} className="flex-1 text-sm font-semibold" style={{ color: colors.text.primary }}>
                    {labelForField(fieldKey)}
                  </Text>
                  {conflicted && (
                    <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: colors.amber.light }}>
                      <Text numberOfLines={1} className="text-xs font-semibold" style={{ color: colors.amber.main }}>
                        Sources disagree
                      </Text>
                    </View>
                  )}
                </View>

                {candidates.length === 0 && (
                  <>
                    <Text className="text-sm leading-5" style={{ color: colors.text.tertiary }}>
                      Nothing found for this field. It stays empty until you provide it — an empty value
                      is never filled in with a zero or a default.
                    </Text>
                    {canEdit && (
                      <FounderAnswerInput
                        fieldKey={fieldKey}
                        value={answers[fieldKey] ?? ''}
                        onChange={(text) => setAnswers((current) => ({ ...current, [fieldKey]: text }))}
                        colors={colors}
                      />
                    )}
                  </>
                )}

                {/* EVERY candidate is shown, including the ones not chosen. A
                    contrary value that disappears from the screen is a conflict
                    resolved by hiding it. */}
                {candidates.map((candidate) => {
                  const isSelected = selection?.candidateId === candidate.candidateId;
                  return (
                    <CandidateRow
                      key={candidate.candidateId}
                      candidate={candidate}
                      selected={isSelected}
                      canEdit={canEdit}
                      onSelect={() => select(fieldKey, candidate)}
                      onInspect={onInspectSource
                        ? () => onInspectSource(fieldKey, candidate.candidateId)
                        : undefined}
                      colors={colors}
                    />
                  );
                })}

                {chosen && needsPublicationDecision(chosen) && canEdit && (
                  <PublicationToggle
                    value={selection?.publishToInvestors ?? false}
                    visibility={chosen.visibility}
                    onToggle={() => togglePublish(fieldKey)}
                    colors={colors}
                  />
                )}
              </View>
            );
          })}
        </View>
      ))}

      {canEdit && onSave && (
        <View className="gap-2">
          <View className="flex-row items-start gap-2">
            <Info size={14} color={colors.text.tertiary} />
            <Text className="flex-1 text-xs leading-4" style={{ color: colors.text.tertiary }}>
              Saving creates a new setup version and marks any analysis built on the old one as
              needing a refresh. It does not run an analysis and does not use one of your monthly
              runs.
            </Text>
          </View>
          <View
            className="rounded-xl overflow-hidden"
            style={{ height: 48, backgroundColor: colors.primary[600], opacity: isSaving ? 0.6 : 1 }}
          >
            <AnimatedPressable
              onPress={() => onSave(Object.values(selections), founderInputs())}
              disabled={isSaving}
              accessibilityLabel="Save analysis inputs"
              className="items-center justify-center"
              style={{ flex: 1 }}
            >
              <Text numberOfLines={1} className="text-base font-semibold" style={{ color: colors.text.inverse }}>
                {isSaving ? 'Saving…' : 'Save analysis inputs'}
              </Text>
            </AnimatedPressable>
          </View>
        </View>
      )}
    </View>
  );
}

function Header({
  colors,
  version,
  subtitle,
}: {
  colors: ReturnType<typeof useThemeColors>;
  version?: number;
  subtitle: string;
}) {
  return (
    <View className="gap-1.5">
      <View className="flex-row items-center gap-2">
        <Text numberOfLines={1} className="flex-1 text-xl font-bold" style={{ color: colors.text.primary }}>
          Analysis setup
        </Text>
        {typeof version === 'number' && (
          <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: colors.surface.secondary }}>
            <Text numberOfLines={1} className="text-xs font-semibold" style={{ color: colors.text.secondary }}>
              v{version}
            </Text>
          </View>
        )}
      </View>
      <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
        {subtitle}
      </Text>
    </View>
  );
}

function CandidateRow({
  candidate,
  selected,
  canEdit,
  onSelect,
  onInspect,
  colors,
}: {
  candidate: DraftCandidate;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onInspect?: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const body = (
    <View className="gap-2">
      <View className="flex-row items-start gap-2">
        <View
          className="items-center justify-center rounded-full shrink-0"
          style={{
            width: 20,
            height: 20,
            marginTop: 2,
            backgroundColor: selected ? colors.primary[600] : colors.surface.secondary,
            borderWidth: selected ? 0 : 1,
            borderColor: colors.border.medium,
          }}
        >
          {selected && <Check size={13} color={colors.text.inverse} />}
        </View>
        <Text className="flex-1 text-sm leading-5" style={{ color: colors.text.primary }}>
          {summarizeValue(candidate.value)}
        </Text>
      </View>

      <View className="flex-row flex-wrap gap-1.5" style={{ paddingLeft: 28 }}>
        <Chip label={ORIGIN_LABELS[candidate.sourceKind]} colors={colors} />
        <Chip label={PRECISION_LABELS[candidate.provenancePrecision]} colors={colors} />
        <Chip label={ASSERTION_LABELS[candidate.assertionStatus]} colors={colors} />
        <Chip label={VISIBILITY_LABELS[candidate.visibility]} colors={colors} />
        {/* Corroboration is shown as corroboration, never as proof — and two
            providers returning one source were already collapsed into one
            group before this arrived. */}
        {candidate.corroboratingGroups.length > 0 && (
          <Chip
            label={`Also in ${candidate.corroboratingGroups.length} other source${candidate.corroboratingGroups.length === 1 ? '' : 's'}`}
            colors={colors}
          />
        )}
      </View>

      <Text className="text-xs leading-4" style={{ color: colors.text.tertiary, paddingLeft: 28 }}>
        {PRECISION_EXPLANATIONS[candidate.provenancePrecision]}
      </Text>

      {onInspect && (
        <Text
          accessibilityRole="button"
          onPress={onInspect}
          className="text-sm font-semibold"
          style={{ color: colors.primary[700], paddingLeft: 28 }}
        >
          View source
        </Text>
      )}
    </View>
  );

  if (!canEdit) {
    return (
      <View className="rounded-lg p-2.5" style={{ backgroundColor: colors.surface.secondary }}>
        {body}
      </View>
    );
  }

  return (
    <View
      className="rounded-lg overflow-hidden"
      style={{
        backgroundColor: selected ? colors.primary[50] : colors.surface.secondary,
        borderWidth: 1,
        borderColor: selected ? colors.primary[200] : 'transparent',
      }}
    >
      <AnimatedPressable
        onPress={onSelect}
        accessibilityRole="radio"
        accessibilityState={{ selected }}
        accessibilityLabel={`Use ${summarizeValue(candidate.value)} from ${ORIGIN_LABELS[candidate.sourceKind]}`}
        className="p-2.5"
      >
        {body}
      </AnimatedPressable>
    </View>
  );
}

function PublicationToggle({
  value,
  visibility,
  onToggle,
  colors,
}: {
  value: boolean;
  visibility: DraftCandidate['visibility'];
  onToggle: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="rounded-lg p-2.5 gap-2" style={{ backgroundColor: colors.amber.light }}>
      <Text className="text-xs leading-4" style={{ color: colors.text.secondary }}>
        This value came from {VISIBILITY_LABELS[visibility].toLowerCase()}. It is used in an
        investor-visible analysis only if you allow it.
      </Text>
      <View
        className="rounded-lg overflow-hidden"
        style={{ height: 40, backgroundColor: value ? colors.primary[600] : colors.surface.primary }}
      >
        <AnimatedPressable
          onPress={onToggle}
          accessibilityRole="switch"
          accessibilityState={{ checked: value }}
          accessibilityLabel="Allow this value in investor-visible analyses"
          className="flex-row items-center justify-center gap-2"
          style={{ flex: 1, minHeight: 40 }}
        >
          {value && <Check size={14} color={colors.text.inverse} />}
          <Text
            numberOfLines={1}
            className="text-sm font-semibold"
            style={{ color: value ? colors.text.inverse : colors.text.secondary }}
          >
            {value ? 'Investors may see this' : 'Allow investors to see this'}
          </Text>
        </AnimatedPressable>
      </View>
    </View>
  );
}

/**
 * The founder's own answer for a field no source could supply.
 *
 * A list field says so in its hint and is split on commas at save time — the
 * research projection reads those fields as arrays and drops anything else, so
 * a sentence here would be stored and then silently ignored.
 */
function FounderAnswerInput({
  fieldKey,
  value,
  onChange,
  colors,
}: {
  fieldKey: string;
  value: string;
  onChange: (text: string) => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const isList = LIST_FIELDS.has(fieldKey);
  const label = labelForField(fieldKey);

  return (
    <View className="gap-1.5">
      <FormInput
        value={value}
        onChangeText={onChange}
        accessibilityLabel={`Your answer for ${label}`}
        placeholder={isList ? 'Separate each one with a comma' : `Your ${label.toLowerCase()}`}
        multiline={!isList}
      />
      {isList && (
        <Text className="text-xs leading-4" style={{ color: colors.text.tertiary }}>
          Separate each one with a comma.
        </Text>
      )}
    </View>
  );
}

function Chip({ label, colors }: { label: string; colors: ReturnType<typeof useThemeColors> }) {
  return (
    <View className="rounded-full px-2 py-1" style={{ backgroundColor: colors.surface.primary }}>
      <Text numberOfLines={1} className="text-xs" style={{ color: colors.text.secondary }}>
        {label}
      </Text>
    </View>
  );
}
